/**
 * 🗺️ Map Page
 * Stadium map with POI markers
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IceCream, User, LogOut, Accessibility, Locate, Cross } from 'lucide-react';
import styles from './Map.module.css';

// 🔧 DEV MODE - Set to true to drag markers and see coordinates
const DEV_MODE = false;

type FilterType = 'all' | 'food' | 'bathroom' | 'exit' | 'accessible' | 'firstaid';

const filters = [
  { type: 'food' as FilterType, icon: IceCream, label: 'Food' },
  { type: 'bathroom' as FilterType, icon: User, label: 'Bath' },
  { type: 'exit' as FilterType, icon: LogOut, label: 'Exit' },
  { type: 'accessible' as FilterType, icon: Accessibility, label: 'Acc' },
  { type: 'firstaid' as FilterType, icon: Cross, label: 'Aid' },
];

const POIS = [
  { id: 'FM1', name: 'Frosty Mango - Main', type: 'food', x: 27, y: 71 },
  { id: 'FM2', name: 'Frosty Mango - North', type: 'food', x: 84, y: 41 },
  { id: 'B1', name: 'Restroom A', type: 'bathroom', x: 35, y: 43 },
  { id: 'B2', name: 'Restroom B', type: 'bathroom', x: 76, y: 66 },
  { id: 'E1', name: 'Exit 1', type: 'exit', x: 28, y: 59 },
  { id: 'E2', name: 'Exit 2', type: 'exit', x: 83, y: 55 },
  { id: 'E3', name: 'Exit 3', type: 'exit', x: 50, y: 20 },
  { id: 'E4', name: 'Exit 4', type: 'exit', x: 50, y: 80 },
  { id: 'A1', name: 'Accessible Entrance', type: 'accessible', x: 80, y: 20 },
  { id: 'FA1', name: 'First Aid - North', type: 'firstaid', x: 31, y: 20 },
  { id: 'FA2', name: 'First Aid - South', type: 'firstaid', x: 77, y: 85 },
];

const markerColors: Record<string, string> = {
  food: '#F47920',
  bathroom: '#3498DB',
  exit: '#2ECC71',
  accessible: '#9B59B6',
  firstaid: '#E74C3C',
};

const markerEmojis: Record<string, string> = {
  food: '🍦',
  bathroom: '🚻',
  exit: '🚪',
  accessible: '♿',
  firstaid: '🏥',
};

export function MapPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [selectedPOI, setSelectedPOI] = useState<typeof POIS[0] | null>(null);

  // DEV MODE: Draggable markers
  const [devPositions, setDevPositions] = useState<Record<string, {x: number, y: number}>>(
    Object.fromEntries(POIS.map(poi => [poi.id, { x: poi.x, y: poi.y }]))
  );
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  const handleDragStart = (poiId: string) => {
    if (!DEV_MODE) return;
    setDraggingId(poiId);
  };

  const handleDrag = (e: React.MouseEvent | React.TouchEvent) => {
    if (!DEV_MODE || !draggingId || !mapRef.current) return;

    const rect = mapRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const x = Math.round(((clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((clientY - rect.top) / rect.height) * 100);

    setDevPositions(prev => ({
      ...prev,
      [draggingId]: { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) }
    }));
  };

  const handleDragEnd = () => {
    if (draggingId) {
      const pos = devPositions[draggingId];
      console.log(`📍 ${draggingId}: x: ${pos.x}, y: ${pos.y}`);
    }
    setDraggingId(null);
  };

  const filteredPOIs = activeFilter === 'all'
    ? POIS
    : POIS.filter(poi => poi.type === activeFilter);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Stadium Map</h1>
      </header>

      {/* Map Area */}
      <div className={styles.mapContainer}>
        <div
          ref={mapRef}
          className={styles.map}
          onMouseMove={handleDrag}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchMove={handleDrag}
          onTouchEnd={handleDragEnd}
        >
          {/* Stadium Map Image */}
          <img
            src="/stad.jpg"
            alt="Stadium Map"
            className={styles.stadiumImage}
          />

          {/* User location - arrow shows facing direction */}
          <div className={styles.userMarker} style={{ left: '50%', top: '60%' }}>
            <div
              className={styles.userArrow}
              style={{ transform: 'rotate(-45deg)' }} /* Point toward top-left as example */
            />
          </div>

          {/* POI Markers */}
          {filteredPOIs.map((poi) => {
            const pos = DEV_MODE ? devPositions[poi.id] : { x: poi.x, y: poi.y };
            return (
              <button
                key={poi.id}
                className={`${styles.marker} ${selectedPOI?.id === poi.id ? styles.markerSelected : ''} ${DEV_MODE ? styles.markerDraggable : ''}`}
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  backgroundColor: markerColors[poi.type],
                  cursor: DEV_MODE ? 'grab' : 'pointer',
                }}
                onMouseDown={() => handleDragStart(poi.id)}
                onTouchStart={() => handleDragStart(poi.id)}
                onClick={() => !DEV_MODE && setSelectedPOI(poi)}
              >
                {markerEmojis[poi.type]}
              </button>
            );
          })}

          {/* DEV MODE: Coordinate display */}
          {DEV_MODE && draggingId && (
            <div className={styles.devCoords}>
              {draggingId}: x: {devPositions[draggingId]?.x}, y: {devPositions[draggingId]?.y}
            </div>
          )}
        </div>

        {/* Center on user button */}
        <button className={styles.locateButton}>
          <Locate size={20} />
        </button>

        {/* Selected POI Card */}
        {selectedPOI && (
          <div className={styles.poiCard}>
            <span 
              className={styles.poiIcon}
              style={{ backgroundColor: markerColors[selectedPOI.type] }}
            >
              {markerEmojis[selectedPOI.type]}
            </span>
            <div className={styles.poiInfo}>
              <span className={styles.poiName}>{selectedPOI.name}</span>
              <span className={styles.poiType}>{selectedPOI.type}</span>
            </div>
            <button 
              className={styles.poiDirections}
              onClick={() => navigate(`/directions?type=${selectedPOI.type}`)}
            >
              Go
            </button>
            <button 
              className={styles.poiClose}
              onClick={() => setSelectedPOI(null)}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        {filters.map((filter) => {
          const Icon = filter.icon;
          const isActive = activeFilter === filter.type;
          return (
            <button
              key={filter.type}
              className={`${styles.filterButton} ${isActive ? styles.filterButtonActive : ''}`}
              onClick={() => setActiveFilter(isActive ? 'all' : filter.type)}
            >
              <Icon size={18} />
              <span>{filter.label}</span>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: '#F47920' }} />
          <span>Food</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: '#3498DB' }} />
          <span>Bath</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: '#2ECC71' }} />
          <span>Exit</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: '#E74C3C' }} />
          <span>Aid</span>
        </div>
      </div>
    </div>
  );
}
